import _ from 'lodash'
import {
  hasPatientOnBoard,
  normalizePatientList,
  createEmptyPatientInfo,
  patientTransportStatuses,
  resolvePatientTransportStatus,
  isPatientVisibleInView,
} from '@/helpers/patient'
import orderTypes from '@/dictionaries/orderTypes'

const normalizeWorkflowSnapshotTeam = team => {
  if (!team || typeof team !== 'object') {
    return {}
  }

  const normalizedTeamDefinition = (() => {
    if (team.teamDefinition && typeof team.teamDefinition === 'object') {
      return team.teamDefinition
    }

    if (Object.prototype.hasOwnProperty.call(team, 'code')) {
      return {
        code: team.code,
      }
    }

    return undefined
  })()

  return {
    ...team,
    ...(normalizedTeamDefinition ? { teamDefinition: normalizedTeamDefinition } : {}),
  }
}

const updatePatientInfoState = state => {
  const normalizedPatients = normalizePatientList(state.orderEdited.patientInfo)
  const patientlessTypes = [orderTypes.event, orderTypes.medical_courier]
  const shouldSkipPatients = patientlessTypes.includes(state.orderEdited.type)
  if (shouldSkipPatients) {
    state.orderEdited.patientInfo = normalizedPatients && normalizedPatients.length
      ? normalizedPatients
      : []
    state.orderEdited.patientOnBoard = hasPatientOnBoard(state.orderEdited.patientInfo)
    return
  }

  state.orderEdited.patientInfo = normalizedPatients && normalizedPatients.length
    ? normalizedPatients
    : [createEmptyPatientInfo()]
  state.orderEdited.patientOnBoard = hasPatientOnBoard(state.orderEdited.patientInfo)
}

const createDefaultTransportHistoryState = () => ({
  loading: false,
  error: false,
  initialized: false,
  count: 0,
  displayCount: '0',
  items: [],
  truncated: false,
  modalVisible: false,
  requestToken: 0,
})

const normalizeOrderVersionField = order => {
  if (!order || typeof order !== 'object') {
    return order
  }

  const normalizedOrder = _.cloneDeep(order)

  if (
    !Object.prototype.hasOwnProperty.call(normalizedOrder, 'version')
    && Object.prototype.hasOwnProperty.call(normalizedOrder, 'workflowVersion')
  ) {
    normalizedOrder.version = normalizedOrder.workflowVersion
  }

  if (Object.prototype.hasOwnProperty.call(normalizedOrder, 'workflowVersion')) {
    delete normalizedOrder.workflowVersion
  }

  if (
    Object.prototype.hasOwnProperty.call(normalizedOrder, 'customFieldValues')
    && (!normalizedOrder.customFieldValues || typeof normalizedOrder.customFieldValues !== 'object' || Array.isArray(normalizedOrder.customFieldValues))
  ) {
    normalizedOrder.customFieldValues = {}
  }

  return normalizedOrder
}

export default {
  SET_ORDER_EDITED(state, order) {
    state.orderEdited = _.merge(_.cloneDeep(state.defaultOrder), normalizeOrderVersionField(order))
    updatePatientInfoState(state)
  },
  SET_ORDER_WORKFLOW(state, workflow) {
    state.orderWorkflow = workflow || null

    const workflowVersion = Number(workflow?.orderVersion)
    if (Number.isFinite(workflowVersion)) {
      state.orderEdited.version = workflowVersion
    }
  },
  MERGE_ORDER_EDITED(state, order) {
    state.orderEdited = _.merge(state.orderEdited, normalizeOrderVersionField(order))
    updatePatientInfoState(state)
  },
  APPLY_ORDER_SAVE_GENERATED_VALUES(state, payload) {
    if (!payload || typeof payload !== 'object') {
      return
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'version')) {
      const currentVersion = Number(state.orderEdited.version)
      const nextVersion = Number(payload.version)
      if (!Number.isFinite(currentVersion) || nextVersion >= currentVersion) {
        state.orderEdited.version = payload.version
      }
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'updatedAt')) {
      state.orderEdited.updatedAt = payload.updatedAt
    }

    if (!Array.isArray(payload.patientInfo) || !Array.isArray(state.orderEdited.patientInfo)) {
      return
    }

    state.orderEdited.patientInfo = state.orderEdited.patientInfo.map(patient => {
      const generatedPatient = payload.patientInfo.find(item => item?.clientUid && item.clientUid === patient?.clientUid)
      if (!generatedPatient) {
        return patient
      }

      return {
        ...patient,
        id: generatedPatient.id ?? patient.id,
        version: generatedPatient.version ?? patient.version,
      }
    })

    if (!Array.isArray(payload.comments) || !Array.isArray(state.orderEdited.comments)) {
      return
    }

    state.orderEdited.comments = state.orderEdited.comments.map((comment, index) => {
      const generatedComment = payload.comments.find(item => item?.index === index)
      if (!generatedComment) {
        return comment
      }

      return {
        ...comment,
        id: generatedComment.id ?? comment.id,
      }
    })
  },
  APPLY_ORDER_WORKFLOW_SNAPSHOT(state, snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
      return
    }

    if (Object.prototype.hasOwnProperty.call(snapshot, 'status')) {
      state.orderEdited.status = snapshot.status
    }

    if (Object.prototype.hasOwnProperty.call(snapshot, 'version')) {
      state.orderEdited.version = snapshot.version
    } else if (Object.prototype.hasOwnProperty.call(snapshot, 'workflowVersion')) {
      state.orderEdited.version = snapshot.workflowVersion
    }

    if (Object.prototype.hasOwnProperty.call(snapshot, 'patientOnBoard')) {
      state.orderEdited.patientOnBoard = snapshot.patientOnBoard
    }

    if (Object.prototype.hasOwnProperty.call(snapshot, 'team')) {
      if (!snapshot.team) {
        state.orderEdited.team = {}
      } else {
        state.orderEdited.team = _.merge({}, state.orderEdited.team || {}, normalizeWorkflowSnapshotTeam(snapshot.team))
      }
    }

    if (Array.isArray(snapshot.teamAssignments)) {
      state.orderEdited.teamAssignments = _.cloneDeep(snapshot.teamAssignments)
    }

    if (Array.isArray(snapshot.patientInfo) && Array.isArray(state.orderEdited.patientInfo)) {
      state.orderEdited.patientInfo = state.orderEdited.patientInfo.map(patient => {
        const nextPatient = snapshot.patientInfo.find(item => String(item.id) === String(patient.id))
        return nextPatient ? { ...patient, ...nextPatient } : patient
      })
      updatePatientInfoState(state)
    }

    if (Array.isArray(snapshot.orderActivities)) {
      state.orderEdited.orderActivities = _.cloneDeep(snapshot.orderActivities)
    }
  },
  ADD_ORDER_EDITED_ACTIVITY(state, activity) {
    state.orderEdited.orderActivities.push(activity)
  },
  SET_ORDER_EDITED_STATUS(state, status) {
    state.orderEdited.status = status
  },
  PUSH_PROVIDED_HEALTH_SERVICE_TO_ORDER_EDITED(state, service) {
    const targetPatient = state.orderEdited.patientInfo[0]
    if (targetPatient) {
      targetPatient.providedHealthServices.services.push(service)
    }
  },
  REMOVE_PROVIDED_HEALTH_SERVICE_TO_ORDER_EDITED(state, key) {
    const targetPatient = state.orderEdited.patientInfo[0]
    if (targetPatient) {
      targetPatient.providedHealthServices.services.splice(key, 1)
    }
  },
  SET_PATIENT_ON_BOARD(state, payload) {
    const patients = state.orderEdited.patientInfo
    if (!Array.isArray(patients) || !patients.length) {
      state.orderEdited.patientOnBoard = false
      return
    }
    const direction = typeof state.orderEdited.multiTransportDirection === 'string'
      ? state.orderEdited.multiTransportDirection.toLowerCase()
      : null
    const isInboundBulkDeliveryWithoutPatientId = state.orderEdited.type === orderTypes.multi_transport
      && direction === 'inbound'
      && payload
      && payload.status === 'patient_delivery'
      && payload.patientOnBoard === false
      && !payload.patientId
    const isOutboundBulkPickupWithoutPatientId = state.orderEdited.type === orderTypes.multi_transport
      && direction === 'outbound'
      && payload
      && payload.status === 'patient_pick_up'
      && payload.patientOnBoard === true
      && !payload.patientId

    if (isInboundBulkDeliveryWithoutPatientId) {
      const nextPatients = patients.map(patient => {
        if (
          patient
          && isPatientVisibleInView(patient)
          && resolvePatientTransportStatus(patient) === patientTransportStatuses.onBoard
        ) {
          return {
            ...patient,
            patientOnBoard: false,
            patientTransportStatus: patientTransportStatuses.delivered,
          }
        }
        return patient
      })
      state.orderEdited.patientInfo = nextPatients
      state.orderEdited.patientOnBoard = hasPatientOnBoard(nextPatients)
      return
    }

    if (isOutboundBulkPickupWithoutPatientId) {
      const nextPatients = patients.map(patient => {
        if (
          patient
          && isPatientVisibleInView(patient)
          && resolvePatientTransportStatus(patient) === patientTransportStatuses.pending
        ) {
          return {
            ...patient,
            patientOnBoard: true,
            patientTransportStatus: patientTransportStatuses.onBoard,
          }
        }

        return patient
      })
      state.orderEdited.patientInfo = nextPatients
      state.orderEdited.patientOnBoard = hasPatientOnBoard(nextPatients)
      return
    }

    if (payload?.status === 'waiting_for_return_pickup' && payload?.patientOnBoard === false) {
      const nextPatients = patients.map(patient => {
        if (!isPatientVisibleInView(patient)) {
          return patient
        }

        return {
          ...patient,
          patientOnBoard: false,
        }
      })
      state.orderEdited.patientInfo = nextPatients
      state.orderEdited.patientOnBoard = false
      return
    }

    const resolvedTransportStatus = (() => {
      if (payload?.status === 'patient_pick_up') {
        return patientTransportStatuses.onBoard
      }
      if (payload?.status === 'return_patient_pick_up') {
        return patientTransportStatuses.onBoard
      }
      if (payload?.status === 'patient_delivery') {
        return patientTransportStatuses.delivered
      }
      if (payload?.status === 'patient_not_taken') {
        return patientTransportStatuses.notTaken
      }
      if (payload?.status === 'patient_left_at_destination') {
        return patientTransportStatuses.delivered
      }
      if (payload?.patientOnBoard === true) {
        return patientTransportStatuses.onBoard
      }
      if (payload?.patientOnBoard === false) {
        return patientTransportStatuses.pending
      }
      return null
    })()

    const shouldUpdateAll = !payload || !payload.patientId
      || !patients.some(patient => patient.id === payload.patientId)
    const nextPatients = patients.map(patient => {
      const shouldUpdateThisPatient = shouldUpdateAll
        || patient.id === payload.patientId
      if (!isPatientVisibleInView(patient)) {
        return patient
      }
      if (!shouldUpdateThisPatient) {
        return patient
      }
      return {
        ...patient,
        patientOnBoard: resolvedTransportStatus
          ? resolvedTransportStatus === patientTransportStatuses.onBoard
          : resolvePatientTransportStatus(patient) === patientTransportStatuses.onBoard,
        patientTransportStatus: resolvedTransportStatus || resolvePatientTransportStatus(patient),
      }
    })
    state.orderEdited.patientInfo = nextPatients
    state.orderEdited.patientOnBoard = hasPatientOnBoard(nextPatients)
  },
  CLEAR_ORDER_EDITED(state) {
    state.orderEdited = _.cloneDeep(state.defaultOrder)
    state.orderWorkflow = null
    state.transportHistory = createDefaultTransportHistoryState()
  },
  SET_ORDERS(state, orders) {
    state.orders = orders
  },
  SET_FREE_ORDERS(state, orders) {
    state.freeOrders = orders
  },
  SET_ORDER_STATUSES(state, orderStatuses) {
    state.orderStatuses = orderStatuses
  },
  UPDATE_ORDER(state, data) {
    for (let i = 0; i < Object.entries(data).length; i += 1) {
      const [key, value] = Object.entries(data)[i]
      state.orderEdited[key] = value
    }
  },
  SET_ORDER_PICKUP_ADDRESS(state, pickupAddress) {
    state.orderEdited.pickupAddress = pickupAddress
  },
  SET_ORDER_DELIVERY_ADDRESS(state, deliveryAddress) {
    state.orderEdited.deliveryAddress = deliveryAddress
  },
  SET_ORDER_CUSTOMER_ADDRESS(state, customerAddress) {
    state.orderEdited.customerAddress = customerAddress
  },
  SET_ORDER_STATUS(state, newStatus) {
    state.orderEdited.status = newStatus
  },
  SET_ORDER_TEAM(state, newTeam) {
    state.orderEdited.team = newTeam
  },
  REMOVE_ORDER_RECORD(state, itemId) {
    const orderIndex = state.orders.findIndex(u => u.id === itemId)
    state.orders.splice(orderIndex, 1)
  },
  SET_ORDER_STATISTICS_BY_START_AT(state, statistics) {
    state.statisticsByStartAt = statistics
  },
  RESET_ORDER_TRANSPORT_HISTORY(state) {
    const currentRequestToken = state.transportHistory.requestToken
    state.transportHistory = {
      ...createDefaultTransportHistoryState(),
      requestToken: currentRequestToken,
    }
  },
  SET_ORDER_TRANSPORT_HISTORY_REQUEST_TOKEN(state, requestToken) {
    state.transportHistory.requestToken = requestToken
  },
  SET_ORDER_TRANSPORT_HISTORY_LOADING(state, loading) {
    state.transportHistory.loading = loading
    if (loading) {
      state.transportHistory.error = false
    }
  },
  SET_ORDER_TRANSPORT_HISTORY_RESULT(state, payload) {
    state.transportHistory.loading = false
    state.transportHistory.error = false
    state.transportHistory.initialized = true
    state.transportHistory.count = payload.count
    state.transportHistory.displayCount = payload.displayCount
    state.transportHistory.items = Array.isArray(payload.items) ? payload.items : []
    state.transportHistory.truncated = !!payload.truncated
  },
  SET_ORDER_TRANSPORT_HISTORY_ERROR(state, hasError) {
    state.transportHistory.loading = false
    state.transportHistory.error = hasError
    state.transportHistory.initialized = false
  },
  SET_ORDER_TRANSPORT_HISTORY_MODAL_VISIBLE(state, visible) {
    state.transportHistory.modalVisible = visible
  },
  ADD_ORDER_COMMENT(state, comment) {
    state.orderEdited.comments.push(comment)
  },
}
